"use client"

import { useEffect, useRef } from "react"
import Link from "next/link"
import { CheckCircle, Star, ArrowRight, MapPin } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { SocialLoginButtons } from "@/components/auth/SocialLoginButtons"
import { stashEmailCaptureAttribution, trackEvent } from "@/lib/analytics"
import { AppDownloadTextLink } from "@/components/ui/app-download-cta"
import type { SavedPlanAnalyticsContext } from "@/lib/saved-plan-analytics"

interface CarePlanSentSuccessProps {
  /** Identified grass name, when reachable in the flow state. */
  grassType?: string
  /** Region label (e.g. "Transition Zone"), when available. */
  region?: string
  /** First 3 digits of the ZIP, used for the "tuned to your ZIP" line. */
  zipPrefix?: string
  /** Email the plan was just sent to, for the confirmation line. */
  email?: string
  /**
   * Primary action — advances the conversion triad to the 12-month plan.
   * When provided, renders as an explicit button so the user owns the
   * transition instead of being bounced. The hosting flow still auto-advances
   * after a short beat as a fallback for users who don't click.
   */
  onSeePlan?: () => void
  seePlanHref?: string
  oauthRedirectTo?: string
  savedPlanContext?: SavedPlanAnalyticsContext
  onOAuthClick?: () => void
  className?: string
}

/**
 * Post-capture success frame for the homepage grass-ID care-plan gate.
 *
 * Built to the NGL success-screen pattern (the bar): a celebratory checkmark +
 * status line, a live social-proof line, then a value-forward primary CTA that
 * advances the user, with a quiet reassurance. The anti-pattern is a generic
 * "thank you" that dead-ends — here the email-capture moment is treated as the
 * peak of the flow (growth-design rule 1), the copy names the grass + ZIP
 * (rule 2), and the unlocked value is shown as scannable chips, not a paragraph
 * (rule 3). Visual language is the canonical success frame (green circle +
 * CheckCircle + Star), originally shared with the retired /email-capture
 * page (deleted 2026-07-11).
 */
export function CarePlanSentSuccess({
  grassType,
  region,
  zipPrefix,
  email,
  onSeePlan,
  seePlanHref,
  oauthRedirectTo,
  savedPlanContext,
  onOAuthClick,
  className,
}: CarePlanSentSuccessProps) {
  const oauthShownRef = useRef(false)
  const heading = grassType
    ? `Your ${grassType} plan is on the way`
    : "Your care plan is on the way"

  // Specific one-line summary of what was just unlocked. Reuses data already in
  // the flow state — grass name + region/ZIP — and never fabricates a number.
  const summary = grassType
    ? region
      ? `12 months of timed tasks for your ${grassType} lawn in the ${region}.`
      : zipPrefix
        ? `12 months of timed tasks for your ${grassType} lawn near ${zipPrefix}xx.`
        : `12 months of timed tasks for your ${grassType} lawn.`
    : "12 months of timed tasks for your lawn and season."

  useEffect(() => {
    if (!oauthRedirectTo || oauthShownRef.current) return
    oauthShownRef.current = true
    trackEvent('post_capture_oauth_shown', { surface: 'care_plan_sent_success' })
  }, [oauthRedirectTo])

  const handleOAuthBeforeRedirect = () => {
    stashEmailCaptureAttribution('post_capture_success')
    onOAuthClick?.()
  }

  return (
    <div
      className={cn(
        "relative w-full max-w-full min-w-0 overflow-hidden rounded-2xl border border-green-200 bg-linear-to-br from-green-50 to-emerald-50 p-6 md:p-8 text-center shadow-sm",
        className,
      )}
    >
      {/* Celebratory checkmark — the canonical capture-success frame. */}
      <div className="flex justify-center mb-4">
        <div className="relative">
          <div className="p-4 bg-green-100 rounded-full shadow-md">
            <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-green-600" strokeWidth={2} />
          </div>
          <Star className="absolute -top-1.5 -right-1.5 w-5 h-5 md:w-6 md:h-6 text-amber-400" />
        </div>
      </div>

      <h3 className="text-xl md:text-2xl font-extrabold tracking-tight text-gray-900 leading-tight mb-2">
        {heading}
      </h3>

      {/* Specific summary of the unlocked value. */}
      <p className="text-[13.5px] md:text-sm text-gray-700 leading-snug max-w-md mx-auto mb-1">
        {summary}
      </p>
      <p className="text-[12.5px] text-gray-500 leading-snug max-w-md mx-auto mb-4">
        {email ? (
          <>
            Check <span className="font-semibold text-gray-700">{email}</span> in the next few
            minutes.
          </>
        ) : (
          "Check your inbox in the next few minutes."
        )}
      </p>

      {/* Reinforce the saved value without an unverified customer count. */}
      <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-green-200 bg-white/70 px-3 py-1">
        <MapPin className="w-3.5 h-3.5 text-green-700" strokeWidth={2.25} />
        <span className="text-[12px] font-semibold text-green-800">
          Your grass, ZIP code, and plan stay connected
        </span>
      </div>

      {/* Primary value-forward action — advances the triad to the 12-month
          plan. The hosting flow auto-advances after a beat too, so this button
          just lets impatient users skip the wait. */}
      {onSeePlan && (seePlanHref ? (
        <Button asChild className="group w-full max-w-sm mx-auto bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 md:py-4 px-6 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 min-h-[48px]">
          <Link href={seePlanHref} onClick={onSeePlan}>
            See my care plan
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </Button>
      ) : (
        <Button
          onClick={onSeePlan}
          className="group w-full max-w-sm mx-auto bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 md:py-4 px-6 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 min-h-[48px]"
        >
          See my care plan
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
        </Button>
      ))}

      {oauthRedirectTo && (
        <div className="mt-5 mx-auto max-w-sm">
          <p className="mb-2 text-[13px] font-semibold leading-snug text-green-800">
            You are one tap from never losing this plan
          </p>
          <SocialLoginButtons
            redirectTo={oauthRedirectTo}
            savedPlanContext={savedPlanContext}
            compact
            onBeforeRedirect={handleOAuthBeforeRedirect}
          />
        </div>
      )}

      {onSeePlan && !oauthRedirectTo && (
        <p className="mt-3 text-[11px] text-gray-400">
          Taking you there now…
        </p>
      )}

      {/* Web → app funnel: quiet post-capture app link. The see-plan / OAuth
          asks above own this frame; this is a text-only third path that
          self-gates to null pre-launch. */}
      <div className="mt-4">
        <AppDownloadTextLink
          placement="care_plan_sent_success"
          label={
            grassType
              ? `Get ${grassType} reminders on your phone — free iPhone app`
              : "Get your reminders on your phone — free iPhone app"
          }
          className="text-[13px]"
        />
      </div>
    </div>
  )
}
